import { useState } from "react";
import { ArrowRight, Mail, Phone } from "lucide-react";
import { isContactSatisfied } from "@autopic/shared";
import { Button } from "../../components/Button";
import { StepHeader } from "../../components/StepHeader";
import { TextField } from "../../components/Field";
import { TrustStrip } from "../../components/TrustStrip";
import { useSession } from "../../store/sessionStore";
import type { StepProps } from "../stepTypes";
import flow from "../BookingFlow.module.css";
import s from "../steps.module.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+0-9()\s-]{7,}$/;

/** Step 4 — capture contact per strategy. Creating the lead happens via saveLead. */
export function ContactStep({ onNext, saveLead }: StepProps) {
  const config = useSession((st) => st.config)!;
  const strategy = config.flow.contactStrategy;
  const contact = useSession((st) => st.contact);
  const setContact = useSession((st) => st.setContact);

  const [email, setEmail] = useState(contact.email ?? "");
  const [phone, setPhone] = useState(contact.phone ?? "");
  const [touched, setTouched] = useState(false);

  const wantEmail = strategy === "email" || strategy === "both" || strategy === "either";
  const wantPhone = strategy === "phone" || strategy === "both" || strategy === "either";

  const emailValid = !email || EMAIL_RE.test(email.trim());
  const phoneValid = !phone || PHONE_RE.test(phone.trim());

  const candidate = {
    email: emailValid && email ? email.trim() : undefined,
    phone: phoneValid && phone ? phone.trim() : undefined,
  };
  const satisfied = isContactSatisfied(strategy, candidate);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!satisfied || !emailValid || !phoneValid) return;
    setContact({ email: candidate.email, phone: candidate.phone });
    saveLead("contact"); // creates the lead now that we have contact
    onNext();
  }

  const emailError =
    touched && !emailValid
      ? "Enter a valid email"
      : touched && strategy !== "phone" && !candidate.email && (strategy === "email" || strategy === "both")
        ? "Email is required"
        : null;

  const phoneError =
    touched && !phoneValid
      ? "Enter a valid phone number"
      : touched && strategy !== "email" && !candidate.phone && (strategy === "phone" || strategy === "both")
        ? "Phone is required"
        : null;

  const eitherError =
    touched && strategy === "either" && !satisfied
      ? "Enter an email or a phone number"
      : null;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <StepHeader
        kicker="Stay in the loop"
        title={config.copy.contactTitle}
        subtitle={
          strategy === "either"
            ? "Email or mobile — whichever suits you. We'll send your quote and confirmation here."
            : "We'll send your quote and booking confirmation here."
        }
        display={false}
      />

      <div className={s.stack}>
        {wantEmail && (
          <TextField
            label="Email address"
            type="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError}
            icon={<Mail size={19} />}
            autoComplete="email"
          />
        )}
        {wantPhone && (
          <TextField
            label="Mobile number"
            type="tel"
            inputMode="tel"
            placeholder="07123 456789"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={phoneError}
            icon={<Phone size={18} />}
            autoComplete="tel"
          />
        )}
        {eitherError && (
          <p role="alert" style={{ color: "var(--ap-danger)", fontSize: 13, fontWeight: 600 }}>
            {eitherError}
          </p>
        )}
      </div>

      <div className={s.actions}>
        <Button type="submit" block disabled={!satisfied} trailingIcon={<ArrowRight size={18} />}>
          {config.copy.ctaContinue}
        </Button>
      </div>

      {config.trust.enabled && (
        <div className={flow.trustWrap} style={{ marginTop: 20 }}>
          <TrustStrip trust={config.trust} />
        </div>
      )}
      <p className={flow.footerNote} style={{ marginTop: 14 }}>
        We'll only use your details for your quote, booking and reminders.
      </p>
    </form>
  );
}
